import React, { createContext, useContext, useState, useMemo } from 'react';
import { createTheme, ThemeProvider } from '@mui/material/styles';

const ThemeContext = createContext();

// Cores baseadas na logo: marrom escuro, dourado/bronze, branco
const colors = {
  // Marrom escuro (fundo da logo)
  darkBrown: '#3E2723', // Marrom muito escuro
  brown: '#5D4037', // Marrom médio
  lightBrown: '#8D6E63', // Marrom claro
  
  // Dourado/Bronze (emblema da logo)
  gold: '#D4AF37', // Dourado
  bronze: '#CD7F32', // Bronze
  lightGold: '#F4D03F', // Dourado claro
  
  // Neutros
  white: '#FFFFFF',
  lightGray: '#F5F5F5',
  gray: '#9E9E9E',
  darkGray: '#424242',
  black: '#212121'
};

// Tema Claro
const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: colors.darkBrown, // Marrom escuro
      light: colors.brown,
      dark: '#2E1B17',
      contrastText: colors.white,
    },
    secondary: {
      main: colors.gold, // Dourado
      light: colors.lightGold,
      dark: colors.bronze,
      contrastText: colors.darkBrown,
    },
    background: {
      default: colors.white,
      paper: colors.lightGray,
    },
    text: {
      primary: colors.darkBrown,
      secondary: colors.brown,
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 600,
    },
    h5: {
      fontWeight: 600,
    },
  },
});

// Tema Meio Escuro
const mediumTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: colors.darkBrown,
      light: colors.brown,
      dark: '#2E1B17',
      contrastText: colors.white,
    },
    secondary: {
      main: colors.gold,
      light: colors.lightGold,
      dark: colors.bronze,
      contrastText: colors.darkBrown,
    },
    background: {
      default: '#E8E8E8', // Cinza médio claro
      paper: colors.white,
    },
    text: {
      primary: colors.darkBrown,
      secondary: colors.brown,
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 600,
    },
    h5: {
      fontWeight: 600,
    },
  },
});

// Tema Escuro
const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: colors.gold, // Dourado como primário no tema escuro
      light: colors.lightGold,
      dark: colors.bronze,
      contrastText: colors.darkBrown,
    },
    secondary: {
      main: colors.white,
      light: colors.lightGray,
      dark: colors.gray,
      contrastText: colors.darkBrown,
    },
    background: {
      default: colors.darkBrown, // Marrom escuro como fundo
      paper: colors.brown, // Marrom médio para papers
    },
    text: {
      primary: colors.white,
      secondary: colors.lightGold,
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 600,
    },
    h5: {
      fontWeight: 600,
    },
  },
});

export function ThemeContextProvider({ children }) {
  const [themeMode, setThemeMode] = useState(() => {
    return localStorage.getItem('themeMode') || 'light';
  });

  const theme = useMemo(() => {
    switch (themeMode) {
      case 'medium':
        return mediumTheme;
      case 'dark':
        return darkTheme;
      default:
        return lightTheme;
    }
  }, [themeMode]);

  const toggleTheme = (mode) => {
    setThemeMode(mode);
    localStorage.setItem('themeMode', mode);
  };

  return (
    <ThemeContext.Provider value={{ themeMode, toggleTheme, colors }}>
      <ThemeProvider theme={theme}>
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  );
}

export function useThemeContext() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useThemeContext must be used within ThemeContextProvider');
  }
  return context;
}

